"use client";

import { cn } from "@jesus-central-church/shared";
import { motion, useReducedMotion } from "motion/react";
import { Calendar, Clock, MapPin } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";

interface TimeUnit {
  value: number;
  label: string;
}

interface MobileNextServiceBannerProperties {
  /** Title of the service (e.g., "Sunday Service") */
  title?: string;
  /** Target date/time for the countdown */
  serviceDate: Date;
  /** Location of the service */
  location?: string;
  /** Enable/disable animations */
  enableAnimations?: boolean;
  /** Custom content to render (e.g., a link/button) */
  children?: ReactNode;
  /** Additional CSS classes */
  className?: string;
}

const getTimeRemaining = (targetDate: Date): number => {
  return Math.max(0, Math.floor((+targetDate - Date.now()) / 1000));
};

const getTimeUnits = (totalSeconds: number): TimeUnit[] => {
  const days = Math.floor(totalSeconds / 86_400);
  const hours = Math.floor((totalSeconds % 86_400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return [
    { value: days, label: "Dagen" },
    { value: hours, label: "Uren" },
    { value: minutes, label: "Min" },
    { value: seconds, label: "Sec" },
  ];
};

const formatServiceDate = (date: Date): string => {
  return date.toLocaleDateString("nl-NL", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
};

const formatServiceTime = (date: Date): string => {
  return date.toLocaleTimeString("nl-NL", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

/**
 * MobileNextServiceBanner - Elegant centered countdown for mobile
 *
 * A refined, luxurious banner with prominent countdown boxes,
 * mirroring the desktop design language.
 */
export function MobileNextServiceBanner({
  title = "Sunday Service",
  serviceDate,
  location,
  enableAnimations = true,
  children,
  className,
}: MobileNextServiceBannerProperties) {
  const [timeLeft, setTimeLeft] = useState(() => getTimeRemaining(serviceDate));
  const shouldReduceMotion = useReducedMotion();
  const shouldAnimate = enableAnimations && !shouldReduceMotion;

  useEffect(() => {
    const update = () => {
      setTimeLeft(getTimeRemaining(serviceDate));
    };

    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [serviceDate]);

  const timeUnits = getTimeUnits(timeLeft);
  const isUrgent = timeLeft > 0 && timeLeft < 86_400;
  const hasStarted = timeLeft === 0;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 12 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring" as const,
        stiffness: 400,
        damping: 30,
      },
    },
  };


  return (
    <motion.div
      data-slot="mobile-next-service-banner"
      initial={shouldAnimate ? "hidden" : "visible"}
      animate="visible"
      variants={containerVariants}
      className={cn(
        "relative w-full bg-boldness overflow-hidden",
        className
      )}
    >
      {/* Content container - compact but elegant */}
      <div className="relative px-4 py-4 flex flex-col items-center gap-3">
        {/* Header row: Icon + Title + Badge */}
        <motion.div
          variants={itemVariants}
          className="flex items-center justify-center gap-2.5"
        >
          <div className="flex items-center gap-1.5 text-freedom/60">
            <Clock className="w-3.5 h-3.5" />
            <span className="text-xs font-medium tracking-wide">
              Next: {title}
            </span>
          </div>
          {isUrgent && (
            <span className="relative">
              <span className="absolute inset-0 bg-herstel/30 blur-md rounded-full" />
              <span className="relative bg-herstel text-boldness px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider">
                Soon!
              </span>
            </span>
          )}
        </motion.div>

        {/* Countdown Display - The Hero Element */}
        {hasStarted ? (
          <motion.div
            variants={itemVariants}
            className="flex items-center justify-center gap-2 py-2"
          >
            <span className="relative flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-green-500" />
            </span>
            <span className="text-sm font-bold text-green-400 uppercase tracking-widest">
              Live Now
            </span>
          </motion.div>
        ) : (
          <motion.div
            variants={itemVariants}
            className="flex justify-center gap-2"
          >
            {timeUnits.map((unit, index) => {
              const isSeconds = index === 3;
              const shouldGlow = isSeconds && isUrgent;

              return (
                <div
                  key={unit.label}
                  className="relative"
                >
                  {/* Glow effect for urgent seconds */}
                  {shouldGlow && (
                    <div className="absolute -inset-0.5 bg-herstel/20 blur-lg rounded-xl" />
                  )}

                  <div
                    className={cn(
                      "relative flex flex-col items-center justify-center",
                      "w-[58px] h-[62px] rounded-lg",
                      "border backdrop-blur-sm",
                      "transition-all duration-300",
                      shouldGlow
                        ? "border-herstel/50 bg-herstel/10"
                        : "border-freedom/10 bg-freedom/[0.03]"
                    )}
                  >
                    <motion.span
                      key={unit.value}
                      initial={shouldAnimate ? { scale: 1.1, opacity: 0 } : undefined}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ type: "spring", stiffness: 500, damping: 30 }}
                      className={cn(
                        "text-[22px] font-bold tabular-nums leading-none tracking-tight",
                        shouldGlow ? "text-herstel" : "text-freedom"
                      )}
                    >
                      {unit.value.toString().padStart(2, "0")}
                    </motion.span>
                    <span
                      className={cn(
                        "mt-1 text-[8px] font-semibold uppercase tracking-[0.12em]",
                        shouldGlow ? "text-herstel/70" : "text-freedom/40"
                      )}
                    >
                      {unit.label}
                    </span>
                  </div>
                </div>
              );
            })}
          </motion.div>
        )}

        {/* Date & Location - Subtle info row */}
        <motion.div
          variants={itemVariants}
          className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[11px] text-freedom/40"
        >
          <div className="flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            <span className="capitalize">{formatServiceDate(serviceDate)}</span>
            <span className="text-freedom/20">·</span>
            <span>{formatServiceTime(serviceDate)}</span>
          </div>
          {location && (
            <div className="flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              <span>{location}</span>
            </div>
          )}
        </motion.div>

        {/* Optional children */}
        {children && (
          <motion.div variants={itemVariants}>
            {children}
          </motion.div>
        )}
      </div>

      {/* Bottom accent line */}
      <div className="h-px bg-gradient-to-r from-transparent via-herstel/40 to-transparent" />
    </motion.div>
  );
}
